import { useEffect, useState } from 'react'
import {
  clearVideoTranscodePending,
  getVideoTranscodePending,
  VIDEO_TRANSCODE_WATCH_TIMEOUT_MS
} from '@paintscout/util/s3/util/videoTranscodePending'
import { getS3VideoUrl } from './file-type-utils'

/**
 * Reload an S3 video once its H.264 transcode lands (#22523, #22546).
 *
 * **The problem.** `videoTranscode` rewrites the canonical S3 object in place, so the document's `src`
 * never changes and nothing tells the browser the bytes did. A `<video>` element that mounted while the
 * job was still running keeps the HEVC bytes it already demuxed: audio plays over a black frame, and the
 * tile has no first frame. Closing and reopening the dialog fixed it only because that mounts a new
 * element. `processFile` cannot help — it invokes the task Lambda with `InvocationType: 'Event'` and
 * returns `true` immediately, so it has no job id to hand back, and `QuoteFile` has no status field to
 * poll.
 *
 * **How this closes it.** `uploadFile` records the object in `videoTranscodePending` when it requests the
 * task. This hook polls that object and, once MediaConvert has rewritten it, returns a versioned url.
 * The changed url reloads the element, which fetches the new bytes.
 *
 * **The probe is a one-byte ranged GET, not a HEAD.** The image bucket has two CORS rules: the write rule
 * allows `GET`/`PUT`/`DELETE` from every app origin including `http://localhost:3000`, while the
 * read rule that allows `HEAD` is scoped to `https://*.paintscout.com` (`s3.yml`). S3 matches a request
 * against origin *and* method, so a `HEAD` from a dev origin would be refused. A ranged `GET` is allowed
 * everywhere, returns a `206` with headers, and costs one byte. The body is cancelled unread so a bucket
 * that ever ignored `Range` could not download the whole video.
 *
 * **`fetch` here is not the iPad upload hazard.** `docs/pitfalls/README.md` bans `fetch` for browser *uploads*:
 * WebKit rejects a large cross-origin Blob `PUT` with an opaque "Load failed", which is why
 * `s3UploadFile` uses `putFileViaXhr` (#20385, #21104). That failure needs a request body. This probe
 * sends none.
 *
 * **The same pitfall's Safari cache poisoning does apply, so every probe uses its own url.** Safari
 * shares one cache entry between a CORS and a non-CORS fetch of the same url, which is what
 * `imageCrossOrigin`/`retryCorsImageOnError` exist to work around. This probe is a CORS request to the
 * exact url the `<video>` loads without CORS. The `ps-probe` parameter keeps the two on separate cache
 * keys, so the probe can neither poison nor read the element's entry. `cache: 'no-store'` is kept as
 * well, and S3 ignores the unknown parameter on a public object.
 *
 * **`Last-Modified` is the only header this can read.** A cross-origin response exposes just the
 * CORS-safelisted headers unless the bucket sets `ExposeHeaders`, so `ETag` and `Content-Range` are not
 * available. `Last-Modified` is on the safelist, and it is enough: MediaConvert can only have written the
 * object *after* the transcode was requested, so a timestamp later than `requestedAt` proves the swap.
 * A changed timestamp is checked as well, which covers a device clock running ahead of S3. A clock
 * running *behind* S3 makes the first probe look like a swap: the element reloads once and the watcher
 * stops, which is no worse than the behaviour without this hook.
 *
 * Failure is safe in every direction. A blocked, erroring, or slow-to-finish probe gives up and leaves
 * the file exactly as it behaves today: playable the next time the element mounts.
 */

/** Gap between probes. The object is one byte per probe, so this is cheap enough to keep short. */
const POLL_INTERVAL_MS = 3000

/**
 * Consecutive failed probes before the watcher stops.
 *
 * Guards the case where the object simply cannot be observed from this origin — a CORS rule that does not
 * cover it, or an offline tablet. Without this the watcher would log a failure every three seconds for
 * the whole timeout window.
 */
const MAX_CONSECUTIVE_FAILURES = 3

export interface TranscodedVideoSrc {
  /** The url to render. Carries a `?v=` marker once the transcode landed, so the element reloads. */
  src?: string
  /** A transcode is in flight for this file. Callers use it to show a processing state. */
  transcoding: boolean
}

/** Add the cache-busting version *before* any `#t=` fragment, which must stay at the end of the url. */
function withVersion(src: string, version: number): string {
  const [path, fragment] = src.split('#')
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}v=${version}${fragment ? `#${fragment}` : ''}`
}

/**
 * A url for probe number `attempt`, distinct from both the element's url and every other probe.
 *
 * The counter is the whole point — see the Safari cache-poisoning note above. It is a counter rather
 * than a timestamp so the url is deterministic and assertable in a test.
 */
function probeUrl(src: string, attempt: number): string {
  const separator = src.includes('?') ? '&' : '?'
  return `${src}${separator}ps-probe=${attempt}`
}

export function useTranscodedVideoSrc(file: { src?: string; s3PublicKey?: string }): TranscodedVideoSrc {
  const baseSrc = getS3VideoUrl(file)
  const [watchedSrc, setWatchedSrc] = useState(baseSrc)
  const [version, setVersion] = useState(0)
  const [transcoding, setTranscoding] = useState(() => !!getVideoTranscodePending(baseSrc))

  // React's "adjust state when a prop changes" pattern: reset during render, not in an effect. One
  // component instance renders many files in turn (the dialog preview switches file as the user clicks
  // the strip), and without this the new file inherits the previous file's `?v=` marker and its
  // "processing" state for one frame.
  if (watchedSrc !== baseSrc) {
    setWatchedSrc(baseSrc)
    setVersion(0)
    setTranscoding(!!getVideoTranscodePending(baseSrc))
  }

  useEffect(() => {
    const request = getVideoTranscodePending(baseSrc)
    if (!baseSrc || !request) {
      setTranscoding(false)
      return
    }

    setTranscoding(true)

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let baseline: string | undefined
    let attempt = 0
    let failures = 0
    let lastError: unknown

    // Clears the pending entry, so a later mount does not restart a watch that already gave up.
    const stopWatching = (reason: string) => {
      clearVideoTranscodePending(baseSrc)
      // Logged rather than swallowed: a CORS rule that stops covering this bucket would otherwise make
      // every video upload quietly fail to reload, with no trace anywhere. `console.warn` and not Sentry
      // because this code also runs in painter-view, where Sentry is a no-op.
      console.warn(`useTranscodedVideoSrc: stopped watching ${baseSrc} — ${reason}`, { lastError })
      if (!cancelled) setTranscoding(false)
    }

    const swap = () => {
      clearVideoTranscodePending(baseSrc)
      if (cancelled) return
      setVersion((current) => current + 1)
      setTranscoding(false)
    }

    const probe = async () => {
      if (cancelled) return
      if (Date.now() - request.requestedAt > VIDEO_TRANSCODE_WATCH_TIMEOUT_MS) {
        stopWatching('the transcode did not finish inside the watch window')
        return
      }

      try {
        attempt += 1
        const response = await fetch(probeUrl(baseSrc, attempt), {
          method: 'GET',
          headers: { Range: 'bytes=0-0' },
          cache: 'no-store'
        })
        // Headers are all this needs; drop the body rather than let it buffer.
        response.body?.cancel().catch(() => {
          // A cancel that loses a race with the stream closing is not a probe failure.
        })
        if (cancelled) return

        if (!response.ok) throw new Error(`probe returned ${response.status}`)
        failures = 0

        const header = response.headers.get('last-modified')
        const lastModified = header ? Date.parse(header) : NaN

        if (Number.isFinite(lastModified) && lastModified > request.requestedAt) {
          swap()
          return
        }
        if (baseline === undefined) {
          baseline = header ?? ''
        } else if ((header ?? '') !== baseline) {
          swap()
          return
        }
      } catch (error) {
        lastError = error
        failures += 1
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          stopWatching(`${failures} consecutive probes failed`)
          return
        }
      }

      if (!cancelled) timer = setTimeout(probe, POLL_INTERVAL_MS)
    }

    probe()

    // Unmounting deliberately leaves the pending entry alone: a dialog closed mid-job must still swap
    // when it is reopened. Only stopWatching and a swap clear it, and it expires on its own.
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [baseSrc])

  return { src: baseSrc && version > 0 ? withVersion(baseSrc, version) : baseSrc, transcoding }
}

export default useTranscodedVideoSrc
